import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import api from '@/services/axios'
import { useAuthStore } from './authStore'
import type{ Review, Business } from '@/types'


export const useReviewStore = defineStore('review', () => {
  const authStore = useAuthStore()
  const reviews = ref<Review[]>([])
  const existingReview = ref<Review | null>(null)
  const business = ref<Business | null>(null);
  const businessReviews = ref<Record<number, Review[]>>({})
  const loading = ref(false)
  const error = ref<string | null>(null)
  const submitting = ref(false)

  // Get all reviews for a business
  const getBusinessReviews = async (businessId: number | string) => {
    loading.value = true
    error.value = null

    try {
      const response = await api.get(
        `/api/business/${businessId}/reviews`
      )

      const fetchedReviews = response.data.reviews.data
      reviews.value = fetchedReviews
      existingReview.value = response.data.existingReview
      business.value = response.data.business
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      businessReviews.value[businessId] = fetchedReviews

      return fetchedReviews
    } catch (err) {
      console.error('Error fetching business reviews:', err)
      error.value = 'Failed to load reviews'
      return []
    } finally {
      loading.value = false
    }
  }

  // Create a new review
  const createReview = async (businessId: number | string, reviewData: { rating: number; comment: string }) => {
    submitting.value = true
    error.value = null

    try {
      const response = await api.post(
        `/api/customer/businesses/${businessId}/reviews`,
        reviewData,
      )

      const newReview = response.data.review;
      await getBusinessReviews(businessId);
      return newReview;
    } catch (err) {
      console.error('Error creating review:', err)
      error.value = 'Failed to submit review'
      throw err
    } finally {
      submitting.value = false
    }
  }

  // Update an existing review
  const updateReview = async (reviewId: number, businessId: number | string, reviewData: { rating: number; comment: string }) => {
    submitting.value = true
    error.value = null

    try {
      const response = await api.put(
        `/api/customer/reviews/${reviewId}`,
        reviewData,
      )

      const updatedReview = response.data.review;
      await getBusinessReviews(businessId);
      return updatedReview
    } catch (err) {
      console.error('Error updating review:', err)
      error.value = 'Failed to update review'
      throw err
    } finally {
      submitting.value = false
    }
  }

  // Delete a review
  const deleteReview = async (reviewId: number, businessId: number | string) => {
    submitting.value = true
    error.value = null

    try {
      await api.delete(
        `/api/customer/reviews/${reviewId}`,
      )

      await getBusinessReviews(businessId);
      return true
    } catch (err) {
      console.error('Error deleting review:', err)
      error.value = 'Failed to delete review'
      throw err
    } finally {
      submitting.value = false
    }
  }

  // Check if the current user has reviewed a business
  const getUserReview = computed(() => {
    if (!authStore.user || !reviews.value.length) return null
    return reviews.value.find(review => review.user_id === authStore.user?.id) || null
  })

  return {
    reviews,
    businessReviews,
    loading,
    error,
    submitting,
    existingReview,
    business,
    getBusinessReviews,
    createReview,
    updateReview,
    deleteReview,
    getUserReview
  }
})
